import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AuthApi, Configuration, ResponseError } from '@app/types/sdk';
import type { Me, TokenIssueResult, TokenResponse } from '@app/types/sdk';
import { useConfig } from '@config/provider';
import {
  buildAccessTokenProvider,
  clearStoredTokens,
  persistTokenResponse,
  readStoredTokens,
  type PersistedToken
} from '@app/api/token';

interface AuthSessionState {
  user: Me | null;
  tokens: PersistedToken | null;
  loading: boolean;
}

interface AuthSessionContextValue extends AuthSessionState {
  setSessionTokens: (tokens: TokenResponse) => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshTokens: () => Promise<PersistedToken | null>;
  switchOrganization: (orgId: string) => Promise<void>;
  clearSession: () => void;
}

const AuthSessionContext = createContext<AuthSessionContextValue | undefined>(undefined);

export interface AuthSessionProviderProps {
  children: ReactNode;
}

const REFRESH_THRESHOLD_MS = 60 * 1000;
const REFRESH_ROTATION_GRACE_MS = 250;

export class AuthSessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'AuthSessionExpiredError';
  }
}

const initialState: AuthSessionState = {
  user: null,
  tokens: null,
  loading: false
};

const isInvalidRefreshTokenError = async (error: unknown): Promise<boolean> => {
  if (!(error instanceof ResponseError)) {
    return false;
  }
  if (error.response.status !== 401) {
    return false;
  }

  try {
    const payload = await error.response.clone().json();
    if (!payload || typeof payload !== 'object') {
      return false;
    }

    const title = typeof (payload as { title?: unknown }).title === 'string' ? (payload as { title: string }).title.toLowerCase() : '';
    if (title === 'invalid_refresh_token') {
      return true;
    }

    const type = typeof (payload as { type?: unknown }).type === 'string' ? (payload as { type: string }).type.toLowerCase() : '';
    if (type.includes('invalid_refresh_token')) {
      return true;
    }

    const detail =
      typeof (payload as { detail?: unknown }).detail === 'string' ? (payload as { detail: string }).detail.toLowerCase() : '';
    return detail.includes('refresh token') && (detail.includes('invalid') || detail.includes('expired'));
  } catch {
    return false;
  }
};

export const AuthSessionProvider = ({ children }: AuthSessionProviderProps) => {
  const { apiBaseUrl } = useConfig();
  const [state, setState] = useState<AuthSessionState>(() => ({ ...initialState, tokens: readStoredTokens() }));
  const refreshPromiseRef = useRef<Promise<PersistedToken | null> | null>(null);
  const tokensRef = useRef<PersistedToken | null>(state.tokens);

  const configuration = useMemo(
    () =>
      new Configuration({
        basePath: apiBaseUrl,
        accessToken: buildAccessTokenProvider()
      }),
    [apiBaseUrl]
  );

  const authApi = useMemo(() => new AuthApi(configuration), [configuration]);

  const clearSession = useCallback(() => {
    clearStoredTokens();
    tokensRef.current = null;
    setState({ ...initialState });
  }, []);

  const applySessionTokens = useCallback((tokens: PersistedToken | null) => {
    tokensRef.current = tokens;
    setState((prev) => ({ ...prev, tokens }));
  }, []);

  const recoverRotatedSession = useCallback(
    async (attemptedRefreshToken: string): Promise<PersistedToken | null> => {
      const readRotatedTokens = (): PersistedToken | null => {
        const stored = readStoredTokens();
        if (!stored?.refreshToken || stored.refreshToken === attemptedRefreshToken) {
          return null;
        }
        applySessionTokens(stored);
        return stored;
      };

      const immediate = readRotatedTokens();
      if (immediate) {
        return immediate;
      }

      await new Promise((resolve) => window.setTimeout(resolve, REFRESH_ROTATION_GRACE_MS));
      return readRotatedTokens();
    },
    [applySessionTokens]
  );

  const hasCurrentRefreshToken = useCallback((attemptedRefreshToken: string): boolean => {
    const stored = readStoredTokens();
    return stored?.refreshToken === attemptedRefreshToken;
  }, []);

  const toTokenResponse = useCallback((result: TokenIssueResult): TokenResponse | null => {
    if (result && typeof (result as TokenResponse).accessToken === 'string') {
      return result as TokenResponse;
    }
    return null;
  }, []);

  const refreshSessionTokens = useCallback(async (): Promise<PersistedToken | null> => {
    const currentTokens = readStoredTokens();
    if (currentTokens !== tokensRef.current) {
      applySessionTokens(currentTokens);
    }
    const refreshToken = currentTokens?.refreshToken;
    if (!refreshToken) {
      return null;
    }

    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = (async () => {
        try {
          const result = await authApi.authTokenPost({
            tokenRequest: {
              grantType: 'refresh',
              refreshToken
            }
          });
          const tokenResponse = toTokenResponse(result);
          if (!tokenResponse) {
            throw new Error('Refresh response did not include access token');
          }
          const persisted = persistTokenResponse(tokenResponse);
          applySessionTokens(persisted);
          return persisted;
        } catch (error) {
          console.error('[auth] Token refresh failed', error);
          if (await isInvalidRefreshTokenError(error)) {
            const recovered = await recoverRotatedSession(refreshToken);
            if (recovered) {
              return recovered;
            }
            if (hasCurrentRefreshToken(refreshToken)) {
              clearSession();
            }
          }
          return null;
        } finally {
          refreshPromiseRef.current = null;
        }
      })();
    }

    return refreshPromiseRef.current;
  }, [applySessionTokens, authApi, clearSession, hasCurrentRefreshToken, recoverRotatedSession, toTokenResponse]);

  const refreshSessionForOrg = useCallback(
    async (orgId: string): Promise<PersistedToken> => {
      const targetOrgId = orgId.trim();
      if (!targetOrgId) {
        throw new Error('Organization is required.');
      }

      if (refreshPromiseRef.current) {
        await refreshPromiseRef.current;
      }

      const currentTokens = readStoredTokens();
      if (currentTokens !== tokensRef.current) {
        applySessionTokens(currentTokens);
      }
      const refreshToken = currentTokens?.refreshToken;
      if (!refreshToken) {
        throw new Error('No refresh token is available.');
      }

      const issueOrgSession = async (token: string): Promise<PersistedToken> => {
        const result = await authApi.authTokenPost({
          tokenRequest: {
            grantType: 'refresh',
            refreshToken: token,
            orgId: targetOrgId
          }
        });
        const tokenResponse = toTokenResponse(result);
        if (!tokenResponse) {
          throw new Error('Refresh response did not include access token');
        }
        const persisted = persistTokenResponse(tokenResponse);
        applySessionTokens(persisted);
        return persisted;
      };

      const refreshPromise = (async () => {
        try {
          return await issueOrgSession(refreshToken);
        } catch (error) {
          console.error('[auth] Organization switch failed', error);
          if (await isInvalidRefreshTokenError(error)) {
            const recovered = await recoverRotatedSession(refreshToken);
            if (recovered?.refreshToken) {
              try {
                return await issueOrgSession(recovered.refreshToken);
              } catch (retryError) {
                if (!(await isInvalidRefreshTokenError(retryError))) {
                  throw retryError;
                }
              }
            }
            if (hasCurrentRefreshToken(refreshToken)) {
              clearSession();
              throw new AuthSessionExpiredError();
            }
            throw new Error('Organization switch was interrupted by session rotation. Try again.');
          }
          throw error;
        } finally {
          refreshPromiseRef.current = null;
        }
      })();

      refreshPromiseRef.current = refreshPromise;
      return refreshPromise;
    },
    [applySessionTokens, authApi, clearSession, hasCurrentRefreshToken, recoverRotatedSession, toTokenResponse]
  );

  const fetchCurrentUser = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const currentTokens = tokensRef.current;
      if (currentTokens?.refreshToken && typeof currentTokens.expiresAt === 'number') {
        if (Date.now() >= currentTokens.expiresAt - REFRESH_THRESHOLD_MS) {
          const refreshed = await refreshSessionTokens();
          if (!refreshed) {
            setState((prev) => ({ ...prev, loading: false }));
            return;
          }
        }
      }
      const me = await authApi.authMeGet();
      setState((prev) => ({ ...prev, user: me, loading: false }));
    } catch (error: unknown) {
      if (error instanceof ResponseError && error.response.status === 401) {
        try {
          const refreshed = await refreshSessionTokens();
          if (refreshed) {
            const me = await authApi.authMeGet();
            setState((prev) => ({ ...prev, user: me, loading: false }));
            return;
          }
        } catch (refreshError) {
          console.error('[auth] Failed to refresh session after 401', refreshError);
        }
      }
      console.error('[auth] Failed to load user profile', error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [authApi, refreshSessionTokens]);

  const setSessionTokens = useCallback(
    async (tokens: TokenResponse) => {
      const persisted = persistTokenResponse(tokens);
      tokensRef.current = persisted;
      setState((prev) => ({ ...prev, tokens: persisted }));
      await fetchCurrentUser();
    },
    [fetchCurrentUser]
  );

  const switchOrganization = useCallback(
    async (orgId: string) => {
      await refreshSessionForOrg(orgId);
      await fetchCurrentUser();
    },
    [fetchCurrentUser, refreshSessionForOrg]
  );

  useEffect(() => {
    tokensRef.current = state.tokens;
  }, [state.tokens]);

  useEffect(() => {
    if (state.tokens && !state.user && !state.loading) {
      fetchCurrentUser().catch((error: unknown) => {
        console.error('[auth] hydrate failed', error);
      });
    }
  }, [fetchCurrentUser, state.loading, state.tokens, state.user]);

  const value = useMemo<AuthSessionContextValue>(
    () => ({
      user: state.user,
      tokens: state.tokens,
      loading: state.loading,
      setSessionTokens,
      refreshUser: fetchCurrentUser,
      refreshTokens: refreshSessionTokens,
      switchOrganization,
      clearSession
    }),
    [clearSession, fetchCurrentUser, refreshSessionTokens, setSessionTokens, state.loading, state.tokens, state.user, switchOrganization]
  );

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
};

export const useAuthSession = (): AuthSessionContextValue => {
  const ctx = useContext(AuthSessionContext);
  if (!ctx) {
    throw new Error('useAuthSession must be used within an AuthSessionProvider');
  }
  return ctx;
};
